import React, { useState, useEffect, forwardRef } from 'react';
import './Itinerary.css';
import mockData from './mockData';
import flight from '../assets/i-flight.png';
import transfer from '../assets/i-transfer.png';
import hotel from '../assets/fluent_building-32-filled.png';
import activimg from '../assets/Vector (7).png';

import BASE_URL from '../config';

const Itinerary = forwardRef((props, ref) => {
  const [itineraryData, setItineraryData] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);

  // Fetch itinerary
  useEffect(() => {
    async function fetchItinerary() {
      try {
        const response = await fetch(`${BASE_URL}/api/user_tour/tour_id/itinerary`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();

        if (data && Array.isArray(data.days) && data.days.length > 0) {
          setItineraryData(data.days);
        } else {
          setItineraryData(mockData.days);
        }
      } catch (error) {
        setItineraryData(mockData.days);
      }
    }

    fetchItinerary();
  }, []);

  // Fetch supported activity options with token
  useEffect(() => {
    async function fetchActivityOptions() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await fetch(`${BASE_URL}/api/supported_activity`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) throw new Error('Failed to fetch activity options');
        const data = await res.json();

        setActivityOptions(data);
      } catch (error) {
        console.error('Error loading activities, using fallback.');
        setActivityOptions([
          { name: 'Activity Name 1', price: 100 },
          { name: 'Activity Name 2', price: 150 },
          { name: 'Activity Name 3', price: 200 },
        ]);
      }
    }

    fetchActivityOptions();
  }, []);

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const renderActivities = (allActivities = [], slot) => {
    const slotRanges = {
      morning: { start: 6, end: 12 },
      afternoon: { start: 12, end: 16 },
      evening: { start: 16, end: 21 },
      overnight: { start: 21, end: 24, altStart: 0, altEnd: 6 },
    };

    const getHour = (iso) => new Date(iso).getHours();

    const overlapsSlot = (activity, slot) => {
      const start = getHour(activity.start_time);
      const end = getHour(activity.end_time);

      if (slot === 'overnight') {
        return (
          (start >= 21 && start <= 23) ||
          (end >= 0 && end < 6) ||
          (start > end && (start >= 21 || end < 6))
        );
      } else {
        const { start: slotStart, end: slotEnd } = slotRanges[slot];
        return (
          (start < slotEnd && end > slotStart) ||
          (start > end && (start < slotEnd || end > slotStart))
        );
      }
    };

    const filtered = allActivities.filter((a) => overlapsSlot(a, slot));
    if (!filtered.length) return <div className="activity-cell empty"></div>;

    return (
      <div className="activity-group">
        {filtered.map((activity, idx) => (
          <ActivityCell key={idx + slot} activity={activity} activityOptions={activityOptions} />
        ))}
      </div>
    );
  };

  return (
    <div className="itinerary-outer" ref={ref}>
      <div className="itinerary-container">
        <div className="overview-title">Brief Itinerary</div>
        <div className="itinerary-header">
          <div></div>
          <div className="header-column">MORNING</div>
          <div className="header-column">AFTERNOON</div>
          <div className="header-column">EVENING</div>
          <div className="header-column">NIGHT</div>
        </div>
        {itineraryData.map((day, index) => {
          const allActivities = [
            ...(day.morning || []),
            ...(day.afternoon || []),
            ...(day.evening || []),
            ...(day.overnight || []),
          ];

          return (
            <div key={index} className="day-row">
              <div className="date-cities">
                <div className="date">{formatDate(day.date)}</div>
                <div className="day-name">{day.day}</div>
              </div>
              <div className="activities">{renderActivities(allActivities, 'morning')}</div>
              <div className="activities">{renderActivities(allActivities, 'afternoon')}</div>
              <div className="activities">{renderActivities(allActivities, 'evening')}</div>
              <div className="activities">{renderActivities(allActivities, 'overnight')}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const ActivityCell = ({ activity, activityOptions = [] }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const togglePopup = () => setIsPopupOpen(!isPopupOpen);

  const { type, start_time, end_time, source, destination, detail } = activity;

  const iconMap = {
    FLIGHT: <img src={flight} alt="Flight Icon" className="activity-icon" />,
    LEISURE: '',
    TRANSFER: <img src={transfer} alt="Transfer Icon" className="activity-icon" />,
    ACTIVITY: <img src={activimg} className="activity-icon" alt="Activity" />,
    HOTEL: <img src={hotel} className="activity-icon" alt="Hotel" />,
  };

  const icon = iconMap[type?.toUpperCase()] || '';

  const displayText = selectedActivity
    ? selectedActivity.name
    : detail?.name ||
      (() => {
        const upperType = type?.toUpperCase();
        if (upperType === 'LEISURE') return 'At Leisure';
        if (upperType === 'ACTIVITY') return 'Activity';
        return `${type}`.trim();
      })();

  return (
    <div className="activity-cell">
      <div className="activity-content">
        <span className="icon">{icon}</span>
        <span className="activity-text">{displayText}</span>
      </div>

      {['ACTIVITY', 'LEISURE'].includes(type?.toUpperCase()) && (
        <button className="activity-action" onClick={togglePopup}>
          + Change Activity
        </button>
      )}

      {isPopupOpen && (
        <div className="popup-overlay" onClick={togglePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>Change Activity</h3>
            <div className="search-bar">
              <input type="text" placeholder="Search Preferred Activity" />
              <button>→</button>
            </div>
            <div className="activity-list">
              {activityOptions.map((opt, idx) => (
                <div
                  key={idx}
                  className="activity-item"
                  style={{
                    cursor: 'pointer',
                    background: selectedActivity?.name === opt.name ? '#f0f0f0' : undefined,
                  }}
                  onClick={() => {
                    setSelectedActivity(opt);
                    setIsPopupOpen(false);
                  }}
                >
                  {opt.name}
                  <div>Price: ₹{opt.price}</div>
                </div>
              ))}
            </div>
            <button onClick={togglePopup}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Itinerary;
